import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'What is the best magnifying glass for seniors?',
    a: 'The best magnifying glass for seniors combines hands-free design, 5X magnification, and built-in lighting. The LumaSeeker P2 offers all three: an optical-grade 5X lens, a 360° adjustable stand for hands-free reading, and integrated LED illumination — making it ideal for seniors who read books, newspapers, or medicine labels every day.',
  },
  {
    q: 'What magnification power do seniors need for reading?',
    a: 'Most seniors need between 3X and 7X magnification for comfortable reading. A 5X magnifying glass is the sweet spot for reading books, newspapers, and medicine bottles without distortion. Higher magnification (8X+) suits very small text like maps or fine print, but can be harder to use at normal reading distances.',
  },
  {
    q: 'What is the best magnifying glass for macular degeneration?',
    a: 'For macular degeneration, a hands-free full-page magnifier with 5X magnification and LED lighting works best. It allows the eyes to focus centrally while the magnifier does the work. The LumaSeeker P2 is designed for low-vision users: the wide optical lens covers a full page, and the soft-glow LED reduces eye strain during extended reading.',
  },
  {
    q: 'What is a hands-free magnifying glass and who needs one?',
    a: 'A hands-free magnifying glass sits on an adjustable stand above your reading material, leaving both hands free to turn pages, take notes, or hold a cup. This design is especially helpful for seniors with arthritis, tremors, or anyone who reads for long periods. The LumaSeeker P2 features a 360° adjustable arm that locks securely into any position.',
  },
  {
    q: 'How do I choose a magnifying glass for an elderly parent?',
    a: 'Look for four key features: (1) 5X magnification — powerful enough for most needs without distortion; (2) hands-free stand — no holding required; (3) LED lighting — reduces eye strain in dim rooms; (4) large lens size — covers a full line of text at once. The LumaSeeker P2 meets all four criteria and includes a large-print setup guide written in 24pt font.',
  },
  {
    q: 'Is an LED magnifying glass better for reading?',
    a: 'Yes. Built-in LED illumination significantly reduces eye strain, especially for seniors reading in low-light conditions. LEDs simulate natural daylight, making text appear sharper and higher in contrast. This is particularly beneficial for people with age-related vision changes, cataracts, or macular degeneration.',
  },
  {
    q: 'What is the difference between a page magnifier and a handheld magnifier?',
    a: 'A page magnifier (also called a stand magnifier or full-page magnifier) rests on a surface and magnifies the entire page at once — ideal for reading books, newspapers, or magazines hands-free. A handheld magnifier must be held up and covers only a small area at a time. For daily reading, most seniors prefer a page magnifier for sustained comfort.',
  },
  {
    q: 'Can a magnifying glass help with low vision?',
    a: 'Yes. A high-quality 5X magnifying glass can significantly help people with low vision read text that would otherwise be illegible. Combined with LED illumination and a hands-free stand, it enables comfortable, sustained reading without fatigue. Always consult an eye specialist for a full low-vision assessment.',
  },
];

const schemaMarkup = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

export default function FAQ() {
  useEffect(() => {
    document.title = 'Magnifying Glass for Seniors FAQ | LumaSeeker';
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Answers to common questions about magnifying glasses for seniors, macular degeneration, low vision, and hands-free reading magnifiers.');
  }, []);

  return (
    <div className="min-h-screen bg-surface text-on-surface" style={{ fontFamily: "'Lexend', sans-serif" }}>

      {/* Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }}
      />

      {/* Nav */}
      <header className="w-full sticky top-0 z-50 bg-surface border-b border-outline-variant/20">
        <nav className="flex justify-between items-center px-6 md:px-12 py-5 max-w-screen-2xl mx-auto">
          <Link to="/" className="flex items-center gap-3">
            <img src="/favicon-32x32.png" alt="LumaSeeker" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-wider text-primary uppercase">LumaSeeker</span>
          </Link>
          <Link to="/" className="btn-primary px-5 py-2 font-bold text-sm">
            ← Back to Home
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 py-16 md:py-24 max-w-screen-xl mx-auto">
        <span className="text-secondary font-bold tracking-widest uppercase block mb-4" style={{ fontSize: '0.85rem' }}>
          Buyer's Guide
        </span>
        <h1 className="font-extrabold text-primary mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1.1 }}>
          Magnifying Glass for Seniors:<br />
          <span className="text-secondary">Frequently Asked Questions</span>
        </h1>
        <p className="text-on-surface-variant max-w-2xl leading-relaxed" style={{ fontSize: 'clamp(1.05rem, 1.4vw, 1.25rem)' }}>
          Everything you need to know about choosing the right magnifying glass for seniors, low vision, macular degeneration, and hands-free reading.
        </p>
      </section>

      {/* FAQ List */}
      <section className="px-6 md:px-12 pb-24 max-w-screen-xl mx-auto">
        <div className="space-y-6">
          {faqs.map(({ q, a }, i) => (
            <div key={i} className="bg-surface-container-low rounded-xl p-8 atmospheric-shadow">
              <h2 className="font-bold text-primary mb-4" style={{ fontSize: 'clamp(1.1rem, 1.4vw, 1.3rem)' }}>
                {q}
              </h2>
              <p className="text-on-surface-variant leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.15rem)' }}>
                {a}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-12 text-center" style={{ background: '#002f1d' }}>
          <h2 className="font-bold mb-4" style={{ color: '#baeed1', fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
            Ready to try the LumaSeeker P2?
          </h2>
          <p className="mb-8 max-w-xl mx-auto" style={{ color: 'rgba(186,238,209,0.75)', fontSize: '1.1rem' }}>
            Hands-free 5X magnification with LED lighting and a 90-day comfort guarantee.
          </p>
          <Link to="/#cta">
            <button
              className="rounded-lg font-bold uppercase tracking-tight"
              style={{ background: '#795900', color: '#fff', fontSize: '1.1rem', padding: '1rem 2.5rem' }}
            >
              Order Your P2 Model Today
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-outline-variant/20 px-6 md:px-12 py-8 text-center text-on-surface-variant text-sm">
        © 2026 LumaSeeker. Empowering vision for a brighter tomorrow.
      </footer>
    </div>
  );
}
